"use client";

import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import { useEffect, useState } from "react";

export type WorkCardProps = {
  thumbnail: string;
  title: string;
  year: string;
  creators?: { name: string; role: string }[];
  category?: string;
  type: "output" | "input";
  source?: string;
  sourceUrl?: string;
  episode?: string;
  isbn?: string;
};

// openBD APIから書影URLを取得するカスタムフック
function useBookCover(isbn?: string, category?: string): string | null {
  const [coverUrl, setCoverUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isbn || (category !== "マンガ" && category !== "小説")) return;
    fetch(`https://api.openbd.jp/v1/get?isbn=${isbn}`)
      .then((r) => r.json())
      .then((data) => {
        const url = data?.[0]?.summary?.cover;
        if (url) setCoverUrl(url);
      })
      .catch(() => {});
  }, [isbn, category]);

  return coverUrl;
}

export default function WorkCard({
  thumbnail,
  title,
  year,
  creators,
  category,
  type,
  source,
  sourceUrl,
  episode,
  isbn,
}: WorkCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const coverUrl = useBookCover(isbn, category);

  const hoverDirection = type === "output" ? "hover:-translate-x-1" : "hover:translate-x-1";
  const hoverBorderColor =
    type === "output" ? "hover:border-[#ff6b6b]/50" : "hover:border-[#667eea]/50";

  return (
    <>
      <div
        onClick={() => setIsOpen(true)}
        className={`flex w-32 shrink-0 cursor-pointer flex-col items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-md transition-all duration-300 hover:bg-white/10 lg:w-auto lg:shrink lg:flex-row lg:items-center lg:gap-4 lg:p-5 ${hoverDirection} ${hoverBorderColor}`}
      >
        <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-[#667eea]/30 to-[#764ba2]/30 text-2xl lg:h-20">
          {coverUrl ? (
            <Image
              src={coverUrl}
              alt={title}
              width={64}
              height={80}
              className="size-full object-cover"
              unoptimized
            />
          ) : (
            thumbnail
          )}
        </div>
        <div className="text-center lg:flex-1 lg:text-left">
          {category && (
            <span className="mb-1 inline-block rounded-full border border-[#667eea]/30 bg-[#667eea]/10 px-2 py-0.5 text-[10px] text-[#667eea]">
              {category}
            </span>
          )}
          <div className="mb-1 text-sm font-semibold">{title}</div>
          <div className="mt-2 text-xs text-[#8b92a8] lg:text-sm">{year}</div>
          {creators && creators.length > 0 && (
            <div className="mt-1 flex flex-col gap-0.5">
              {creators.map((c, i) => (
                <div key={i} className="text-xs text-[#8b92a8] lg:text-sm">
                  {c.role}：{c.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 作品詳細モーダル */}
      <Dialog
        open={isOpen}
        onClose={setIsOpen}
        className="relative z-10"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 flex items-center justify-center overflow-y-auto p-4">
          <DialogPanel
            transition
            className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-[#1a1a2e] p-6 shadow-2xl transition data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
          >
            {/* クローズボタン */}
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="absolute right-4 top-4 text-white/50 hover:text-white"
            >
              <XMarkIcon className="size-6" />
            </button>

            {/* モーダル内容 */}
            <div className="flex flex-col items-center">
              {/* サムネイル */}
              <div className="flex size-24 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-[#667eea]/30 to-[#764ba2]/30 text-4xl">
                {coverUrl ? (
                  <Image
                    src={coverUrl}
                    alt={title}
                    width={96}
                    height={96}
                    className="size-full object-cover"
                    unoptimized
                  />
                ) : (
                  thumbnail
                )}
              </div>

              {/* カテゴリ */}
              {category && (
                <span className="mt-3 inline-block rounded-full border border-[#667eea]/30 bg-[#667eea]/10 px-3 py-1 text-xs text-[#667eea]">
                  {category}
                </span>
              )}

              {/* 作品名 */}
              <h3 className="mt-3 text-center text-lg font-bold text-white">{title}</h3>

              {/* year */}
              <p className="text-sm text-[#8b92a8]">{year}</p>

              {/* creators */}
              {creators && creators.length > 0 && (
                <div className="mt-2 flex flex-col items-center gap-0.5">
                  {creators.map((c, i) => (
                    <p key={i} className="text-sm text-[#8b92a8]">
                      {c.role}：{c.name}
                    </p>
                  ))}
                </div>
              )}

              {/* エピソード（INPUTセクションのみ） */}
              {type === "input" && episode && (
                <div className="mt-6 w-full">
                  <p className="mb-1 text-center text-[10px] font-semibold uppercase tracking-widest text-[#667eea]">episode</p>
                  <p className="text-center text-sm leading-relaxed text-[#c4c9d8]">{episode}</p>
                </div>
              )}

              {/* ソース情報（INPUTセクションのみ） */}
              {type === "input" && (source || sourceUrl) && (
                <div className="mt-4 w-full">
                  <p className="mb-1 text-center text-[10px] font-semibold uppercase tracking-widest text-[#667eea]">source</p>
                  {sourceUrl ? (
                    <a
                      href={sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-center text-sm text-[#4ecdc4] underline underline-offset-2 transition-opacity hover:opacity-70"
                    >
                      {source ?? "source"}
                    </a>
                  ) : (
                    <span className="block text-center text-sm text-[#c4c9d8]">{source}</span>
                  )}
                </div>
              )}
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
}
